import { useEffect, useState } from 'react';
import { mediaRepository } from '../../data/repositories/mediaRepository';
import { catalogueMediaUrl } from '../../data/seed/seedCatalogue';
import type { ExerciseRecord } from '../../data/types';
import { useLazyVisible } from './useLazyVisible';
import styles from './ExerciseThumbnail.module.css';

export interface ExerciseThumbnailSource {
  id: ExerciseRecord['id'];
  source: ExerciseRecord['source'];
  mediaIds: ExerciseRecord['mediaIds'];
  thumbnailMediaId: ExerciseRecord['thumbnailMediaId'];
}

/**
 * A small, fitted exercise image — reused everywhere a compact preview of an
 * exercise's demonstration is needed (exercise list rows, the plan builder
 * and plan preview, the active workout's exercise picker/info panel,
 * Personal Best reports, and workout reports). One shared component so every
 * surface stays consistent and there is a single place that resolves "this
 * exercise's image" from the exercise record — never a second, per-screen
 * image system (Master Phase 11 — the exercise is the source of truth for
 * its own demonstration images everywhere they're shown).
 *
 * Resolution order, cheapest and already-on-device first:
 * 1. The user media gallery's chosen cover — a real `Blob` already sitting
 *    in IndexedDB, so it's cheap and synchronous-feeling.
 * 2. A bundled catalogue GIF, for `source: 'exercisedb'` exercises — the
 *    exact same file `ExerciseMedia.tsx`'s detail-view image requests
 *    (`${id}.gif`). The `<img>` has no `src` at all until `useLazyVisible`
 *    reports the element has actually entered the viewport (plus a small
 *    lookahead margin) — on top of native `loading="lazy"`, not instead of
 *    it, since a row that never intersects should never even get a URL to
 *    resolve. This is what keeps rapidly scrolling past hundreds of rows
 *    from requesting hundreds of GIFs: on-demand display of what's
 *    visible, not the bulk prefetch Phase 0 §8.2 warns against.
 * 3. Nothing — renders `null`, the existing empty-state rule ("no image
 *    exists → no image slot", never a broken-image icon).
 */
export function ExerciseThumbnail({
  exercise,
  size = 44,
  className,
}: {
  exercise: ExerciseThumbnailSource;
  size?: number;
  /**
   * Extra class layered onto the shared `.thumb` styling. Optional and
   * additive only, mirroring `HistoryExerciseThumbnail`'s own `className`
   * (see that component's doc comment for why `!important` is the only way
   * for a caller's class to win over this component's own inline `style`).
   * Added for the Personal Record detail view (2026-09-30), whose image
   * needs to be large and responsive rather than the fixed pixel size every
   * other caller still uses.
   */
  className?: string | undefined;
}): React.JSX.Element | null {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [catalogueFailed, setCatalogueFailed] = useState(false);
  const [catalogueImgNode, setCatalogueImgNode] = useState<HTMLImageElement | null>(null);
  const catalogueVisible = useLazyVisible(catalogueImgNode);

  useEffect(() => {
    setCatalogueFailed(false);
  }, [exercise.id]);

  useEffect(() => {
    if (exercise.mediaIds.length === 0) {
      setObjectUrl(null);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    void mediaRepository.listForExercise(exercise.id).then((rows) => {
      if (cancelled) return;
      const coverId = exercise.thumbnailMediaId ?? rows[0]?.id ?? null;
      const cover = rows.find((row) => row.id === coverId) ?? rows[0];
      const blob = cover?.thumbnailBlob ?? (cover?.kind === 'image' ? cover.blob : null);
      if (blob !== null && blob !== undefined) {
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      } else {
        setObjectUrl(null);
      }
    });
    return () => {
      cancelled = true;
      if (createdUrl !== null) URL.revokeObjectURL(createdUrl);
    };
  }, [exercise.id, exercise.mediaIds, exercise.thumbnailMediaId]);

  const style = { width: size, height: size };
  const combinedClassName = className === undefined ? styles.thumb : `${styles.thumb} ${className}`;

  if (exercise.mediaIds.length > 0) {
    if (objectUrl === null) return null;
    return <img src={objectUrl} alt="" width={size} height={size} className={combinedClassName} style={style} />;
  }

  if (exercise.source === 'exercisedb' && !catalogueFailed) {
    return (
      <img
        ref={setCatalogueImgNode}
        src={catalogueVisible ? catalogueMediaUrl(`${exercise.id}.gif`) : undefined}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        className={combinedClassName}
        style={style}
        onError={() => setCatalogueFailed(true)}
      />
    );
  }

  return null;
}

/**
 * A workout report's per-exercise image — the frozen counterpart to
 * {@link ExerciseThumbnail}.
 *
 * Resolves `SessionExerciseRecord.mediaIdsSnapshot[0]` directly by id via
 * `mediaRepository.getById`, which does not filter by live/tombstoned state
 * — so the exact image that was primary when the workout was recorded keeps
 * rendering even if that image has since been reordered, replaced, or
 * removed from the exercise's *current* gallery (Exercise Images feature,
 * 2026-09-14). This is deliberately a separate resolution path from
 * `ExerciseThumbnail`, which always reflects the live exercise and must
 * never be reused for history — the whole point of this component is to
 * NOT track later edits.
 *
 * Falls back to the bundled catalogue GIF (keyed by the still-live exercise
 * id, since that's a static asset rather than user-editable image data) when
 * the snapshot is empty/null — the same "no gallery → catalogue GIF → empty"
 * ladder `ExerciseThumbnail` uses, so a historical catalogue-only exercise
 * still shows something.
 */
export function HistoryExerciseThumbnail({
  mediaIdsSnapshot,
  catalogueExerciseId,
  size = 44,
  className,
}: {
  mediaIdsSnapshot: string[] | null;
  /** The exercise id to use for the catalogue-GIF fallback, or `null` when unknown/not catalogue-sourced. */
  catalogueExerciseId: string | null;
  size?: number;
  /**
   * Extra class layered onto the shared `.thumb` styling. Optional, and
   * additive only — every existing call site keeps the plain fixed-`size`
   * square it already renders. Added for the Workout Complete summary
   * (2026-09-15), whose per-exercise image needs to stay prominent across
   * phone widths rather than the fixed pixel size every other surface uses;
   * that caller's class overrides width/height with `!important` (the only
   * way to win over this component's own inline `style`, which every call
   * site still needs for its intrinsic size before any stylesheet loads).
   */
  className?: string | undefined;
}): React.JSX.Element | null {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [catalogueFailed, setCatalogueFailed] = useState(false);
  const [catalogueImgNode, setCatalogueImgNode] = useState<HTMLImageElement | null>(null);
  const catalogueVisible = useLazyVisible(catalogueImgNode);

  const primaryId = mediaIdsSnapshot?.[0] ?? null;

  useEffect(() => {
    setCatalogueFailed(false);
  }, [catalogueExerciseId]);

  useEffect(() => {
    if (primaryId === null) {
      setObjectUrl(null);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    void mediaRepository.getById(primaryId).then((media) => {
      if (cancelled) return;
      const blob = media?.thumbnailBlob ?? (media?.kind === 'image' ? media.blob : null);
      if (blob !== null && blob !== undefined) {
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      } else {
        setObjectUrl(null);
      }
    });
    return () => {
      cancelled = true;
      if (createdUrl !== null) URL.revokeObjectURL(createdUrl);
    };
  }, [primaryId]);

  const style = { width: size, height: size };
  const combinedClassName = className === undefined ? styles.thumb : `${styles.thumb} ${className}`;

  if (primaryId !== null) {
    if (objectUrl === null) return null;
    return <img src={objectUrl} alt="" width={size} height={size} className={combinedClassName} style={style} />;
  }

  if (catalogueExerciseId !== null && !catalogueFailed) {
    return (
      <img
        ref={setCatalogueImgNode}
        src={catalogueVisible ? catalogueMediaUrl(`${catalogueExerciseId}.gif`) : undefined}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        className={combinedClassName}
        style={style}
        onError={() => setCatalogueFailed(true)}
      />
    );
  }

  return null;
}
